import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useId, useState } from 'react'
import { cn } from '@/lib/utils'

const prices = [62, 58, 55, 53, 54, 60, 74, 92, 98, 88, 72, 58, 46, 41, 44, 63, 89, 112, 128, 131, 117, 96, 81, 70]

const bidHours = new Set([7, 8, 9, 17, 18, 19, 20])

const maxPrice = Math.max(...prices)
const minPrice = Math.min(...prices)
const peakHour = prices.indexOf(maxPrice)

const hourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`

const TradeVisual: FC = (): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const descriptionId = useId()

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            setActive((current) => (current === null ? peakHour : Math.min(prices.length - 1, current + 1)))
        }
        else if (event.key === 'ArrowLeft') {
            event.preventDefault()
            setActive((current) => (current === null ? peakHour : Math.max(0, current - 1)))
        }
        else if (event.key === 'Escape') {
            setActive(null)
        }
    }

    const activeLeft = active === null ? 0 : ((active + 0.5) / prices.length) * 100

    return (
        <div>
            <div className='flex flex-wrap items-center justify-between gap-2'>
                <p className='text-xs font-medium text-foreground'>
                    Day-ahead price, €/MWh
                </p>

                <ul className='flex items-center gap-3 text-[11px] text-muted-foreground'>
                    <li className='flex items-center gap-1.5'>
                        <span aria-hidden='true' className='size-2.5 rounded-[3px] bg-primary' />
                        Bid window
                    </li>
                    <li className='flex items-center gap-1.5'>
                        <span aria-hidden='true' className='size-2.5 rounded-[3px] bg-sage' />
                        Other hours
                    </li>
                </ul>
            </div>

            <div className='relative mt-8'>
                <div
                    role='group'
                    tabIndex={0}
                    aria-roledescription='chart'
                    aria-label='Hourly day-ahead prices with recommended bid windows'
                    aria-describedby={descriptionId}
                    onKeyDown={onKeyDown}
                    onBlur={() => setActive(null)}
                    onPointerLeave={() => setActive(null)}
                    className='flex h-36 items-end gap-[2px] rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4'
                >
                    {prices.map((price, hour) => (
                        <div
                            key={hour}
                            onPointerEnter={() => setActive(hour)}
                            onPointerDown={() => setActive(hour)}
                            className='flex h-full flex-1 items-end justify-center'
                        >
                            <span
                                className={cn(
                                    'relative w-full max-w-[24px] rounded-t-[4px] transition-opacity duration-150',
                                    bidHours.has(hour) ? 'bg-primary' : 'bg-sage',
                                    active !== null && active !== hour && 'opacity-45'
                                )}
                                style={{ height: `${(price / maxPrice) * 100}%` }}
                            >
                                {hour === peakHour && (
                                    <span
                                        aria-hidden='true'
                                        className={cn(
                                            'absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-medium text-foreground transition-opacity',
                                            active !== null && 'opacity-0'
                                        )}
                                    >
                                        €{price}
                                    </span>
                                )}
                            </span>
                        </div>
                    ))}
                </div>

                {active !== null && (
                    <div
                        aria-hidden='true'
                        className={cn(
                            'pointer-events-none absolute -top-7 whitespace-nowrap rounded-lg bg-foreground px-2.5 py-1.5 text-[11px] text-white shadow-lg',
                            activeLeft > 80 ? '-translate-x-full' : activeLeft < 20 ? 'translate-x-0' : '-translate-x-1/2'
                        )}
                        style={{ left: `${activeLeft}%` }}
                    >
                        <span className='font-semibold'>€{prices[active]}</span>
                        <span className='text-white/70'> at {hourLabel(active)}</span>
                        {bidHours.has(active) && <span className='ml-1.5 rounded-full bg-leaf/25 px-1.5 py-px text-[10px] text-leaf'>Bid</span>}
                    </div>
                )}

                <div aria-hidden='true' className='mt-2 flex justify-between text-[11px] tabular-nums text-muted-foreground'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                    <span>23:00</span>
                </div>

                <p id={descriptionId} className='sr-only'>
                    Prices range from €{minPrice} to €{maxPrice} per MWh, peaking at {hourLabel(peakHour)}. Recommended bid windows are 07:00 to 10:00 and 17:00 to 21:00. Use the arrow keys to read each hour.
                </p>

                <p className='sr-only' aria-live='polite'>
                    {active === null ? '' : `${hourLabel(active)}: €${prices[active]} per MWh${bidHours.has(active) ? ', bid window' : ''}.`}
                </p>
            </div>
        </div>
    )
}

export default TradeVisual
